<script setup>
import { computed } from "vue";
import { Line } from "vue-chartjs";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
);

const props = defineProps({
  labels: {
    type: Array,
    default: () => [],
  },
  quantities: {
    type: Array,
    default: () => [],
  },
  amounts: {
    type: Array,
    default: () => [],
  },
});

const currencyFormatter = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const litresFormatter = new Intl.NumberFormat("en-GB", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const chartData = computed(() => ({
  labels: props.labels,
  datasets: [
    {
      label: "Litres",
      data: props.quantities.map((value) => Number(value ?? 0)),
      borderColor: "rgba(14, 116, 144, 0.95)",
      backgroundColor: "rgba(6, 182, 212, 0.12)",
      pointBackgroundColor: "rgba(14, 116, 144, 0.95)",
      tension: 0.3,
      yAxisID: "litres",
      fill: true,
    },
    {
      label: "Spend",
      data: props.amounts.map((value) => Number(value ?? 0)),
      borderColor: "rgba(22, 163, 74, 0.9)",
      backgroundColor: "rgba(34, 197, 94, 0.1)",
      pointBackgroundColor: "rgba(22, 163, 74, 0.9)",
      tension: 0.3,
      yAxisID: "amount",
      fill: false,
    },
  ],
}));

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: "index",
    intersect: false,
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: "#64748b", maxRotation: 0, minRotation: 0 },
    },
    litres: {
      position: "left",
      beginAtZero: true,
      grid: { color: "rgba(148, 163, 184, 0.2)", drawBorder: false },
      ticks: {
        color: "#0f766e",
        callback: (value) => `${litresFormatter.format(value)} L`,
      },
    },
    amount: {
      position: "right",
      beginAtZero: true,
      grid: { drawOnChartArea: false },
      ticks: {
        color: "#166534",
        callback: (value) => currencyFormatter.format(value),
      },
    },
  },
  plugins: {
    legend: { display: true },
    tooltip: {
      callbacks: {
        label: (context) => {
          if (context.dataset.label === "Litres") {
            return `Litres: ${Number(context.parsed.y ?? 0).toFixed(2)} L`;
          }

          return `Spend: ${currencyFormatter.format(context.parsed.y ?? 0)}`;
        },
      },
    },
  },
}));
</script>

<template>
  <div class="w-full">
    <Line :data="chartData" :options="chartOptions" class="h-72" />
  </div>
</template>
